import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/store/auth'

// Layout components
import AuthLayout from '@/layouts/AuthLayout.vue'
import DashboardLayout from '@/layouts/DashboardLayout.vue'

// Auth views
import Login from '@/views/auth/Login.vue'
import Register from '@/views/auth/Register.vue'
import ForgotPassword from '@/views/auth/ForgotPassword.vue'

// Dashboard views
import Dashboard from '@/views/Dashboard.vue'
import Profile from '@/views/Profile.vue'

// Meeting views
import Meetings from '@/views/meetings/Meetings.vue'
import MeetingDetails from '@/views/meetings/MeetingDetails.vue'
import MeetingCalendar from '@/views/meetings/Calendar.vue'
import CreateMeeting from '@/views/meetings/CreateMeeting.vue'
import EditMeeting from '@/views/meetings/EditMeeting.vue'
import AttendanceManagement from '@/views/meetings/AttendanceManagement.vue'

// Document views
import Documents from '@/views/documents/Documents.vue'
import DocumentDetails from '@/views/documents/DocumentDetails.vue'

// Resolution views
import Resolutions from '@/views/resolutions/Resolutions.vue'
import ResolutionDetails from '@/views/resolutions/ResolutionDetails.vue'
import CreateResolution from '@/views/resolutions/CreateResolution.vue'

// Member views
import Members from '@/views/members/Members.vue'
import MemberDetails from '@/views/members/MemberDetails.vue'

// Committee views
import Committees from '@/views/committees/Committees.vue'
import CommitteeDetails from '@/views/committees/CommitteeDetails.vue'

// Report views
import Reports from '@/views/reports/ReportsNew.vue'

// Settings view
import Settings from '@/views/settings/Settings.vue'

// User management view
import UserManagement from '@/views/users/UserManagement.vue'

const routes = [
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        path: 'login',
        name: 'Login',
        component: Login,
        meta: { requiresGuest: true }
      },
      {
        path: 'register',
        name: 'Register',
        component: Register,
        meta: { requiresGuest: true }
      },
      {
        path: 'forgot-password',
        name: 'ForgotPassword',
        component: ForgotPassword,
        meta: { requiresGuest: true }
      }
    ]
  },
  {
    path: '/',
    component: DashboardLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'Dashboard',
        component: Dashboard
      },
      {
        path: 'profile',
        name: 'Profile',
        component: Profile
      },
      {
        path: 'meetings',
        name: 'Meetings',
        component: Meetings,
        meta: { requiresBoardMember: true }
      },
      {
        path: 'meetings/create',
        name: 'CreateMeeting',
        component: CreateMeeting,
        meta: { requiresBoardMember: true }
      },
      {
        path: 'meetings/calendar',
        name: 'MeetingCalendar',
        component: MeetingCalendar,
        meta: { requiresBoardMember: true }
      },
      {
        path: 'meetings/:id',
        name: 'MeetingDetails',
        component: MeetingDetails,
        props: true,
        meta: { requiresBoardMember: true }
      },
      {
        path: 'meetings/:id/edit',
        name: 'EditMeeting',
        component: EditMeeting,
        props: true,
        meta: { requiresAdmin: true }
      },
      {
        path: 'meetings/:id/attendance',
        name: 'AttendanceManagement',
        component: AttendanceManagement,
        props: true,
        meta: { requiresAdmin: true }
      },
      {
        path: 'documents',
        name: 'Documents',
        component: Documents,
        meta: { requiresBoardMember: true }
      },
      {
        path: 'documents/:id',
        name: 'DocumentDetails',
        component: DocumentDetails,
        props: true,
        meta: { requiresBoardMember: true }
      },
      {
        path: 'resolutions',
        name: 'Resolutions',
        component: Resolutions,
        meta: { requiresBoardMember: true }
      },
      {
        path: 'resolutions/create',
        name: 'CreateResolution',
        component: CreateResolution,
        meta: { requiresBoardMember: true }
      },
      {
        path: 'resolutions/:id/edit',
        name: 'EditResolution',
        component: CreateResolution,
        props: true,
        meta: { requiresBoardMember: true }
      },
      {
        path: 'resolutions/:id',
        name: 'ResolutionDetails',
        component: ResolutionDetails,
        props: true,
        meta: { requiresBoardMember: true }
      },
      {
        path: 'members',
        name: 'Members',
        component: Members,
        meta: { requiresBoardMember: true }
      },
      {
        path: 'members/:id',
        name: 'MemberDetails',
        component: MemberDetails,
        props: true,
        meta: { requiresBoardMember: true }
      },
      {
        path: 'committees',
        name: 'Committees',
        component: Committees,
        meta: { requiresBoardMember: true }
      },
      {
        path: 'committees/:id',
        name: 'CommitteeDetails',
        component: CommitteeDetails,
        props: true,
        meta: { requiresBoardMember: true }
      },
      {
        path: 'reports',
        name: 'Reports',
        component: Reports,
        meta: { requiresAdmin: true }
      },
      {
        path: 'settings',
        name: 'Settings',
        component: Settings,
        meta: { requiresAdmin: true }
      },
      {
        path: 'users',
        name: 'UserManagement',
        component: UserManagement,
        meta: { requiresAdmin: true }
      }
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/auth/login'
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  
  // Check if user is authenticated
  if (!authStore.isAuthenticated && authStore.token) {
    try {
      await authStore.getCurrentUser()
    } catch (error) {
      authStore.logout()
    }
  }
  
  // Handle authentication requirements
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next({ name: 'Login', query: { redirect: to.fullPath } })
    return
  }
  
  // Handle guest-only routes
  if (to.meta.requiresGuest && authStore.isAuthenticated) {
    next({ name: 'Dashboard' })
    return
  }
  
  // Handle admin-only routes
  if (to.meta.requiresAdmin && (!authStore.user || !authStore.user.is_admin)) {
    next({ name: 'Dashboard' })
    return
  }

  // Restrict plain users (no board member profile, not admin) to Dashboard + Profile
  if (to.meta.requiresBoardMember) {
    const u = authStore.user
    if (u && !u.is_admin && !u.is_board_member) {
      next({ name: 'Profile' })
      return
    }
  }
  
  next()
})

export default router